const loadedImages = new WeakSet();
const scheduledHydrationRoots = new WeakSet();
const backgroundHydrationDelay = 1800;

const loadDeferredImage = (image) => {
  if (!(image instanceof HTMLImageElement) || loadedImages.has(image)) return;
  const src = image.dataset.bfDeferredProductSrc;

  if (!src) return;

  loadedImages.add(image);
  image.srcset = image.dataset.bfDeferredProductSrcset || '';
  image.src = src;
  image.removeAttribute('data-bf-deferred-product-src');
  image.removeAttribute('data-bf-deferred-product-srcset');
};

const getSlides = (slideshow) =>
  Array.from(slideshow?.querySelectorAll('slideshow-slide:not([hidden])') || []);

const loadImagesIn = (root = document) => {
  root.querySelectorAll('img[data-bf-deferred-product-src]').forEach((image) => {
    loadDeferredImage(image);
  });
};

const loadSelectedSlides = (root = document) => {
  root.querySelectorAll('slideshow-slide[aria-hidden="false"]').forEach((slide) => {
    loadImagesIn(slide);
  });
};

const shouldSkipBackgroundHydration = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  return Boolean(connection?.saveData) || /2g/.test(connection?.effectiveType || '');
};

const loadDeferredProductImages = (root = document) => {
  root.querySelectorAll('img[data-bf-deferred-product-src]').forEach((image) => {
    loadDeferredImage(image);
  });
};

const scheduleBackgroundHydration = (root = document) => {
  if (shouldSkipBackgroundHydration() || scheduledHydrationRoots.has(root)) return;

  scheduledHydrationRoots.add(root);

  window.setTimeout(() => {
    const hydrate = () => loadDeferredProductImages(root);

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(hydrate, { timeout: 2500 });
      return;
    }

    hydrate();
  }, backgroundHydrationDelay);
};

const scheduleBackgroundHydrationAfterLoad = (root = document) => {
  if (document.readyState === 'complete') {
    scheduleBackgroundHydration(root);
    return;
  }

  window.addEventListener('load', () => scheduleBackgroundHydration(root), { once: true });
};

const getCurrentIndex = (slideshow) => {
  const selectedControl = slideshow.querySelector(
    'slideshow-controls [ref="thumbnails[]"][aria-selected="true"], slideshow-controls [ref="dots[]"][aria-selected="true"]'
  );
  const controls = selectedControl
    ? Array.from(selectedControl.parentElement?.children || []).filter((element) =>
        element.matches?.('button[ref="thumbnails[]"], li')
      )
    : [];

  if (selectedControl && controls.length) {
    const control = selectedControl.closest('li') || selectedControl;
    return Math.max(0, controls.indexOf(control));
  }

  const slides = getSlides(slideshow);
  return Math.max(
    0,
    slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false')
  );
};

const preloadAdjacentSlides = (slideshow) => {
  const slides = getSlides(slideshow);
  if (!slides.length) return;

  const currentIndex = getCurrentIndex(slideshow);
  [currentIndex - 1, currentIndex, currentIndex + 1].forEach((index) => {
    const slide = slides[index];
    if (slide) loadImagesIn(slide);
  });
};

const loadTargetFromControl = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const slideshow = target.closest('slideshow-component');
  if (!slideshow) return;

  const button = target.closest('button');
  const action = button?.getAttribute('on:click') || '';
  const selectMatch = action.match(/\/select\/(\d+)/);

  if (selectMatch) {
    const slide = getSlides(slideshow)[Number(selectMatch[1])];
    if (slide) loadImagesIn(slide);
    return;
  }

  if (action.includes('/next') || action.includes('/previous')) {
    preloadAdjacentSlides(slideshow);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      loadSelectedSlides();
      scheduleBackgroundHydrationAfterLoad();
    },
    { once: true }
  );
} else {
  loadSelectedSlides();
  scheduleBackgroundHydrationAfterLoad();
}

document.addEventListener('slideshow:select', (event) => {
  loadImagesIn(event.detail?.slide || event.target);
});
document.addEventListener('pointerdown', loadTargetFromControl, { capture: true });
document.addEventListener('touchstart', loadTargetFromControl, { capture: true, passive: true });
document.addEventListener('shopify:section:load', (event) => {
  loadSelectedSlides(event.target);
  scheduleBackgroundHydration(event.target);
});
